import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, DollarSign, CheckCircle2, FileText, Calendar, Wallet, BellRing, Settings2, Clock } from 'lucide-react';
import Swal from 'sweetalert2';
import UseAxiosSecure from '../hooks/UseAxiosSecure';
import UseAuth from '../hooks/UseAuth';

const SalarySheet = () => {
    const axiosSecure = UseAxiosSecure();
    const queryClient = useQueryClient();
    const { activeBranchId } = UseAuth();

    const currentMonth = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });
    
    const [searchTerm, setSearchTerm] = useState('');
    const [monthYear, setMonthYear] = useState(currentMonth);

    // 1. FETCH SALARY SHEET
    const { data: payrolls = [], isLoading } = useQuery({
        queryKey: ['salary-sheet', activeBranchId, monthYear, searchTerm],
        queryFn: async () => {
            const res = await axiosSecure.get('/hr/payroll/sheet', {
                params: { search: searchTerm, monthYear }
            });
            return res.data?.data || [];
        },
        enabled: !!activeBranchId
    });

    // 2. GENERATE PAYROLL MUTATION
    const generateMutation = useMutation({
        mutationFn: async () => {
            const res = await axiosSecure.post('/hr/payroll/generate', { monthYear });
            return res.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries(['salary-sheet', activeBranchId]);
            Swal.fire({
                title: 'Payroll Generated!',
                text: data.message,
                icon: 'success',
                background: '#1a103c',
                color: '#fff',
                confirmButtonColor: '#db2777'
            });
        },
        onError: (error) => {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to generate payroll', background: '#1a103c', color: '#fff' });
        }
    });

    // 3. PAY SALARY MUTATION
    const payMutation = useMutation({
        mutationFn: async (payrollId) => {
            const res = await axiosSecure.put('/hr/payroll/pay', { payrollId });
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['salary-sheet', activeBranchId]);
            Swal.fire({
                title: 'Paid & Notified! 🔔',
                text: 'The salary has been marked as paid and the employee has been notified.',
                icon: 'success',
                background: '#1a103c',
                color: '#fff',
                confirmButtonColor: '#10b981',
                timer: 2000,
                showConfirmButton: false
            });
        },
        onError: (error) => {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Payment failed', background: '#1a103c', color: '#fff' });
        }
    });

    const handlePay = (payrollId, name, amount) => {
        Swal.fire({
            title: `Pay ${name}?`,
            text: `Confirm payment of $${amount} for ${monthYear}.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, Mark as Paid',
            background: '#1a103c',
            color: '#fff'
        }).then((result) => {
            if (result.isConfirmed) {
                payMutation.mutate(payrollId);
            }
        });
    };

    if (!activeBranchId) return <div className="text-white text-center mt-20">Loading Salary Sheet...</div>;

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            
            {/* --- HEADER & CONTROLS --- */}
            <div className="bg-[#1a103c]/40 backdrop-blur-md p-6 rounded-3xl border border-white/5 flex flex-col xl:flex-row justify-between items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <FileText className="text-pink-500" /> Salary Sheet
                    </h2>
                    <p className="text-slate-400 text-sm mt-1">Review payouts, generate payroll, and issue salaries.</p>
                </div>
                
                <div className="flex flex-col md:flex-row gap-4 w-full xl:w-auto">
                    
                    <button 
                        onClick={() => generateMutation.mutate()}
                        disabled={generateMutation.isLoading}
                        className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-50 text-white text-sm font-bold rounded-xl transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                        {generateMutation.isLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <Settings2 size={18} />}
                        Generate {monthYear}
                    </button>

                    <div className="relative w-full md:w-64 group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-pink-500 transition-colors" size={18} />
                        <input 
                            type="text" 
                            placeholder="Search employee..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-pink-500/50 transition-all"
                        />
                    </div>
                    
                    <div className="relative w-full md:w-48 group">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cyan-500 transition-colors" size={18} />
                        <input 
                            type="text" 
                            value={monthYear}
                            onChange={(e) => setMonthYear(e.target.value)}
                            placeholder="e.g. April 2026"
                            className="w-full bg-[#0f0a1f] border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-sm text-white focus:outline-none focus:border-cyan-500/50 transition-all"
                        />
                    </div>
                </div>
            </div>

            {/* --- TABLE --- */}
            <div className="bg-[#1a103c]/60 backdrop-blur-xl border border-white/5 rounded-3xl overflow-hidden shadow-2xl relative min-h-[300px]">
                {isLoading && (
                    <div className="absolute inset-0 bg-[#1a103c]/80 z-20 flex items-center justify-center">
                        <div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-slate-400 bg-white/5">
                                <th className="p-6 font-semibold">Employee</th>
                                <th className="p-6 font-semibold">Month/Year</th>
                                <th className="p-6 font-semibold text-right">Base Salary</th>
                                <th className="p-6 font-semibold text-right text-red-400">Deductions</th>
                                <th className="p-6 font-semibold text-right text-emerald-400">Net Pay</th>
                                <th className="p-6 font-semibold text-center">Status</th>
                                <th className="p-6 font-semibold text-center">Action</th>
                            </tr>
                        </thead>

                        <tbody className="text-sm">
                            {payrolls.length === 0 && !isLoading && (
                                <tr>
                                    <td colSpan="7" className="p-10 text-center text-slate-500">
                                        <Wallet size={48} className="mx-auto mb-3 opacity-20" />
                                        <p>No payroll records found for {monthYear}.</p>
                                        <p className="text-xs mt-2">Click the green "Generate" button above to calculate salaries for this month.</p>
                                    </td>
                                </tr>
                            )}
                            {payrolls.map((record) => (
                                <tr key={record._id} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-none">
                                    <td className="p-6">
                                        <div className="flex items-center gap-3">
                                            <img 
                                                src={record.employeeId?.userId?.profileImage || `https://ui-avatars.com/api/?name=${record.employeeId?.userId?.name}&background=random`} 
                                                alt="Profile" 
                                                className="w-10 h-10 rounded-xl object-cover border-2 border-white/10"
                                            />
                                            <div>
                                                <h3 className="font-bold text-white">{record.employeeId?.userId?.name || 'Unknown'}</h3>
                                                <div className="text-slate-400 text-xs">{record.employeeId?.jobTitle}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-6 font-medium text-slate-300">{record.monthYear}</td>
                                    <td className="p-6 text-right font-mono text-slate-300">${record.baseSalary?.toFixed(2)}</td>
                                    <td className="p-6 text-right font-mono text-red-400">-${record.unpaidLeaveDeductions?.toFixed(2)}</td>
                                    <td className="p-6 text-right font-mono font-bold text-emerald-400 text-lg">${record.netPay?.toFixed(2)}</td>
                                    
                                    <td className="p-6 text-center">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold border flex w-fit mx-auto items-center gap-1 ${
                                            record.status === 'Paid' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                                        }`}>
                                            {record.status === 'Paid' ? <CheckCircle2 size={12}/> : <Clock size={12}/>}
                                            {record.status.toUpperCase()}
                                        </span>
                                    </td>

                                    <td className="p-6 text-center">
                                        {record.status === 'Pending' ? (
                                            <button 
                                                onClick={() => handlePay(record._id, record.employeeId?.userId?.name, record.netPay)}
                                                className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white text-xs font-bold rounded-lg transition-all shadow-lg active:scale-95 flex items-center gap-1 mx-auto"
                                            >
                                                <DollarSign size={14} /> Pay Now
                                            </button>
                                        ) : (
                                            <span className="text-xs text-slate-500 font-bold flex items-center justify-center gap-1">
                                                <BellRing size={12} /> Notified
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SalarySheet;